import { PayRequestPayload, SubmitType } from "checkout-query-client/api/types";
import { PaymentType } from "checkout-query-client/types/form-types";
import {
  getAcceptedMandates,
  getDeviceFingerprintSessionId,
} from "checkout-query-client/utils/helpers";

import {
  Lpm,
  LpmType,
  PayRequest,
  PaymentMethod,
  PaymentMethod_Type,
  PlainMessage,
  PostalAddress,
} from "frontend-client";

const GUEST_SHOPPER = "Guest Shopper";

export const buildLpmPayload = async (
  payload: PayRequestPayload
): Promise<PayRequest> => {
  const { checkoutSessionData, submitType, orderDigest, lpmPaymentCancelled } =
    payload;
  const request = new PayRequest();
  const lpm = new Lpm({
    lpmType: getLpmTypeFromPaymentType(submitType),
    preferredLanguage: "en",
    billingAddress: getLpmBillingAddress({
      checkoutSessionData,
      submitType,
    }),
  });

  request.paymentMethod = new PaymentMethod({
    lpm,
    type: PaymentMethod_Type.LPM,
  });
  request.acceptedMandates = getAcceptedMandates(payload.formFields);
  request.billingAddressFromIntentBilling = Boolean(
    checkoutSessionData?.billingAddress
  );
  request.orderDigest = orderDigest ?? "";
  request.lpmPaymentCancelled = lpmPaymentCancelled ?? false;
  request.deviceFingerprintSessionId = getDeviceFingerprintSessionId();
  return Promise.resolve(request);
};

const getLpmTypeFromPaymentType = (submitType: SubmitType): LpmType => {
  switch (submitType) {
    case PaymentType.ideal:
      return LpmType.IDEAL;
    case PaymentType.alipay:
      return LpmType.ALI_PAY;
    case PaymentType.paypal:
      return LpmType.PAY_PAL;
    case PaymentType.trustly:
      return LpmType.TRUSTLY;
    case PaymentType.wechatPay:
      return LpmType.WECHAT_PAY;
    default:
      return LpmType.UNSPECIFIED;
  }
};

interface GetLpmBillingAddressParams {
  submitType: SubmitType;
  checkoutSessionData: PayRequestPayload["checkoutSessionData"];
}

const getLpmBillingAddress = ({
  submitType,
  checkoutSessionData,
}: GetLpmBillingAddressParams): PlainMessage<PostalAddress> => {
  switch (submitType) {
    case PaymentType.alipay:
    case PaymentType.wechatPay:
      return {
        ...getDefaults(),
        regionCode:
          checkoutSessionData?.billingAddress?.address?.regionCode || "CN",
        recipients: getRecipients(checkoutSessionData),
      };
    case PaymentType.ideal:
      return {
        ...getDefaults(),
        regionCode:
          checkoutSessionData?.billingAddress?.address?.regionCode || "NL",
        recipients: getRecipients(checkoutSessionData),
      };
    case PaymentType.trustly:
      return {
        ...getDefaults(),
        regionCode:
          checkoutSessionData?.billingAddress?.address?.regionCode || "DE",
        recipients: getRecipients(checkoutSessionData),
      };
    case PaymentType.paypal:
      return {
        ...getDefaults(),
        regionCode:
          checkoutSessionData?.billingAddress?.address?.regionCode || "DK",
        recipients: getRecipients(checkoutSessionData),
      };
    default:
      return {
        ...getDefaults(),
        regionCode:
          checkoutSessionData?.billingAddress?.address?.regionCode || "GB",
        recipients: getRecipients(checkoutSessionData),
      };
  }
};

const getRecipients = (
  checkoutSessionData: PayRequestPayload["checkoutSessionData"]
) => {
  const isRecipientsEmpty =
    (checkoutSessionData?.billingAddress?.address?.recipients ?? []).filter(
      (r) => !!r
    ).length === 0;
  if (isRecipientsEmpty) {
    return [GUEST_SHOPPER];
  }
  return checkoutSessionData?.billingAddress?.address?.recipients ?? [];
};

const getDefaults = () => ({
  addressLines: [],
  administrativeArea: "",
  languageCode: "",
  locality: "",
  organization: "",
  postalCode: "",
  revision: 0,
  sortingCode: "",
  sublocality: "",
});
